import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaChartLine,
    FaChartPie,
    FaCoins,
    FaCrown,
    FaExchangeAlt,
    FaGlobe,
    FaHandHoldingUsd,
    FaLayerGroup,
    FaLeaf,
    FaLock,
    FaPercentage,
    FaRocket,
    FaShieldAlt,
    FaUniversity
} from "react-icons/fa";
import { TiArrowRight, TiArrowUp } from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Investments = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const navigate = useNavigate();

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.2,
            scrollTrigger: {
              trigger: section,
              start: "top 80%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }

    if (scrollTopRef.current) {
      gsap.fromTo(
        scrollTopRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          scrollTrigger: {
            trigger: scrollTopRef.current,
            start: "top bottom",
          },
        }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (
      window.innerHeight + window.scrollY >=
      document.body.offsetHeight - 200
    ) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  // Stats
  const stats = [
    { value: "12%+", label: "Average APY", icon: <FaPercentage /> },
    { value: "£50M+", label: "Total Value Locked", icon: <FaCoins /> },
    { value: "21+", label: "Investment Products", icon: <FaLayerGroup /> },
    { value: "100%", label: "Blockchain Secured", icon: <FaLock /> },
  ];

  // Investment products from productsData
  const investmentProducts = [
    {
      name: "Zero-Stake",
      description: "Staking & liquidity rewards with up to 15% APY. Secure DeFi staking with cross-chain liquidity pools.",
      apy: "8-15%",
      risk: "Medium",
      color: "cyan",
      icon: <FaCoins />,
      productId: 19,
    },
    {
      name: "Zero-Wealth",
      description: "AI-driven wealth management with automated portfolio rebalancing and personalised investment strategies.",
      apy: "6-12%",
      risk: "Low-Medium",
      color: "pink",
      icon: <FaChartPie />,
      productId: 12,
    },
    {
      name: "Zero-Lend",
      description: "Peer-to-peer blockchain lending. Earn interest by lending your assets or borrow at competitive rates.",
      apy: "5-10%",
      risk: "Medium",
      color: "indigo",
      icon: <FaHandHoldingUsd />,
      productId: 9,
    },
    {
      name: "Zero-Green",
      description: "Sustainable investment crowdfunding for eco-friendly projects. Make an impact while earning returns.",
      apy: "4-8%",
      risk: "Low-Medium",
      color: "emerald",
      icon: <FaLeaf />,
      productId: 13,
    },
    {
      name: "Zero-Tokeniser",
      description: "Invest in tokenised real-world assets: real estate, equities, and commodities with fractional ownership.",
      apy: "6-14%",
      risk: "Medium-High",
      color: "orange",
      icon: <FaLayerGroup />,
      productId: 18,
    },
    {
      name: "Zero-Dex",
      description: "Decentralised exchange with advanced liquidity pools. Earn trading fees and LP rewards.",
      apy: "10-20%",
      risk: "High",
      color: "purple",
      icon: <FaExchangeAlt />,
      productId: 20,
    },
  ];

  // Risk levels
  const riskLevels = [
    { level: "Conservative", color: "emerald", description: "Lower risk, steady returns of 4-6% APY", products: ["Zero-Green", "Zero-Wealth"] },
    { level: "Balanced", color: "blue", description: "Moderate risk, balanced returns of 6-12% APY", products: ["Zero-Lend", "Zero-Tokeniser"] },
    { level: "Growth", color: "purple", description: "Higher risk, growth potential of 10-20% APY", products: ["Zero-Stake", "Zero-Dex"] },
  ];

  // Color schemes
  const colorSchemes = {
    cyan: {
      gradient: "from-cyan-500/20 via-cyan-500/5 to-transparent",
      border: "hover:border-cyan-500/50",
      glow: "group-hover:shadow-cyan-500/25",
      icon: "text-cyan-400",
      bg: "bg-cyan-500",
    },
    purple: {
      gradient: "from-purple-500/20 via-purple-500/5 to-transparent",
      border: "hover:border-purple-500/50",
      glow: "group-hover:shadow-purple-500/25",
      icon: "text-purple-400",
      bg: "bg-purple-500",
    },
    blue: {
      gradient: "from-blue-500/20 via-blue-500/5 to-transparent",
      border: "hover:border-blue-500/50",
      glow: "group-hover:shadow-blue-500/25",
      icon: "text-blue-400",
      bg: "bg-blue-500",
    },
    emerald: {
      gradient: "from-emerald-500/20 via-emerald-500/5 to-transparent",
      border: "hover:border-emerald-500/50",
      glow: "group-hover:shadow-emerald-500/25",
      icon: "text-emerald-400",
      bg: "bg-emerald-500",
    },
    orange: {
      gradient: "from-orange-500/20 via-orange-500/5 to-transparent",
      border: "hover:border-orange-500/50",
      glow: "group-hover:shadow-orange-500/25",
      icon: "text-orange-400",
      bg: "bg-orange-500",
    },
    pink: {
      gradient: "from-pink-500/20 via-pink-500/5 to-transparent",
      border: "hover:border-pink-500/50",
      glow: "group-hover:shadow-pink-500/25",
      icon: "text-pink-400",
      bg: "bg-pink-500",
    },
    indigo: {
      gradient: "from-indigo-500/20 via-indigo-500/5 to-transparent",
      border: "hover:border-indigo-500/50",
      glow: "group-hover:shadow-indigo-500/25",
      icon: "text-indigo-400",
      bg: "bg-indigo-500",
    },
  };

  const getRiskColor = (risk) => {
    if (risk.includes("Low")) return "text-emerald-400";
    if (risk.includes("Medium") && !risk.includes("High")) return "text-yellow-400";
    if (risk.includes("High")) return "text-red-400";
    return "text-gray-300";
  };

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section - Full Viewport Height */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/investments-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="investmentsHeroSparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.0}
              particleDensity={30}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Smart Investments"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Strategic Growth · Diversified Portfolios · Real-Time Yield
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          additionalClasses=""
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-20 mx-auto max-w-7xl">
        
        {/* Introduction */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              Grow Your Wealth with DeFi
            </span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            Explore innovative blockchain-powered investment opportunities with transparent yields, smart contract security, and tokenised asset diversity.
          </p>
        </motion.div>

        {/* Stats Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative p-6 bg-black/50 backdrop-blur-sm border border-white/10 rounded-2xl text-center group-hover:border-emerald-500/30 transition-all duration-500">
                  <div className="text-emerald-400 text-2xl mb-3 flex justify-center">{stat.icon}</div>
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-300 mt-2">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Investment Products Section */}
        <div className="mb-20" ref={(el) => (sectionRefs.current[0] = el)}>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-4"
          >
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Investment Products
            </span>
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-gray-300 text-center mb-12 max-w-xl mx-auto"
          >
            Choose from our range of blockchain-powered investment solutions
          </motion.p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {investmentProducts.map((product, index) => {
              const colors = colorSchemes[product.color];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className={`group relative ${colors.glow} hover:shadow-xl transition-all duration-500`}
                >
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-white/10 via-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute -inset-2 rounded-2xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500" />
                  
                  <div className={`relative h-full bg-black/80 backdrop-blur-sm rounded-2xl p-6 border border-white/10 ${colors.border} transition-all duration-500 flex flex-col`}>
                    <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-radial ${colors.gradient} rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className={`relative z-10 w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-xl ${colors.icon} group-hover:scale-110 transition-transform duration-300`}>
                        {product.icon}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-white">{product.apy}</div>
                        <div className="text-xs text-gray-300">APY</div>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <h4 className="relative z-10 text-xl font-bold text-white mb-2">{product.name}</h4>
                    <p className="relative z-10 text-gray-300 text-sm leading-relaxed mb-4 flex-grow">{product.description}</p>
                    
                    {/* Bottom section - stays at bottom */}
                    <div className="mt-auto">
                      {/* Risk Level */}
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs text-gray-500">Risk Level:</span>
                        <span className={`text-sm font-medium ${getRiskColor(product.risk)}`}>{product.risk}</span>
                      </div>
                      
                      {/* CTA Button */}
                      <GlassButton
                        onClick={() => navigate(`/products/${product.productId}`)}
                        className="relative z-10 w-full"
                        variant={product.color}
                        size="sm"
                      >
                        <div className="flex items-center justify-center gap-2">
                          View Product <TiArrowRight className="text-lg" />
                        </div>
                      </GlassButton>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Risk Profiles Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
          ref={(el) => (sectionRefs.current[1] = el)}
        >
          <h3 className="text-3xl font-bold text-center mb-12">
            <span className="bg-gradient-to-r from-emerald-400 via-yellow-400 to-red-400 bg-clip-text text-transparent">
              Choose Your Risk Profile
            </span>
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            {riskLevels.map((profile, index) => {
              const colors = colorSchemes[profile.color];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group relative"
                >
                  <div className={`relative bg-black/50 backdrop-blur-sm rounded-2xl p-8 border border-white/10 ${colors.border} transition-all duration-500 h-full flex flex-col`}>
                    {/* Icon */}
                    <div className={`w-16 h-16 rounded-2xl ${colors.bg}/20 flex items-center justify-center text-3xl ${colors.icon} mb-6`}>
                      {index === 0 ? <FaShieldAlt /> : index === 1 ? <FaChartLine /> : <FaCrown />}
                    </div>
                    
                    <h4 className="text-2xl font-bold text-white mb-2">{profile.level}</h4>
                    <p className="text-gray-300 mb-6 flex-grow">{profile.description}</p>
                    
                    <div className="space-y-2 mt-auto">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Recommended Products:</span>
                      <div className="flex flex-wrap gap-2">
                        {profile.products.map((prod, i) => (
                          <span key={i} className={`px-3 py-1 rounded-full text-xs ${colors.bg}/20 ${colors.icon} border border-white/10`}>
                            {prod}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Why Invest Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20 relative rounded-3xl overflow-hidden"
          ref={(el) => (sectionRefs.current[2] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10" />
          <div className="absolute inset-0 bg-black/80" />
          
          <div className="relative z-10 py-16 px-8">
            <h3 className="text-3xl font-bold text-center mb-12 text-white">
              Why Invest with Zero-Blockchain?
            </h3>
            
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { icon: <FaLock />, title: "Smart Contract Security", desc: "All investments secured by audited smart contracts" },
                { icon: <FaGlobe />, title: "Global Access", desc: "Invest from anywhere, 24/7 with no borders" },
                { icon: <FaChartLine />, title: "Transparent Yields", desc: "Real-time tracking of all returns and fees" },
                { icon: <FaUniversity />, title: "Regulatory Compliant", desc: "Built with KYC/AML compliance in mind" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-2xl text-cyan-400 mx-auto mb-4">
                    {item.icon}
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                  <p className="text-gray-300 text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative rounded-3xl overflow-hidden"
          ref={(el) => (sectionRefs.current[3] = el)}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 via-cyan-500/20 to-blue-500/20" />
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          
          <div className="relative z-10 py-16 px-8 text-center">
            <motion.div
              initial={{ scale: 0.9 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-sm mb-6"
            >
              <FaRocket /> Start Investing Today
            </motion.div>
            
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Grow Your Portfolio?
            </h3>
            <p className="text-gray-300 max-w-2xl mx-auto mb-8">
              Join thousands of investors already earning passive income through Zero-Blockchain's innovative DeFi investment products.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <GlassButton
                onClick={() => navigate('/products')}
                className="px-8 py-4"
                variant="emerald"
                size="lg"
              >
                <div className="flex items-center gap-2">
                  <FaChartLine /> Explore All Products
                </div>
              </GlassButton>
              <GlassButton
                onClick={() => navigate('/banking')}
                className="px-8 py-4"
                variant="gray"
                size="lg"
              >
                <div className="flex items-center gap-2">
                  <FaUniversity /> Open Bank Account
                </div>
              </GlassButton>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50"
          onClick={scrollToTop}
          aria-label="Back to Top"
        >
          <TiArrowUp className="text-cyan-400 transition-all duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider transition-colors duration-500 hover:text-white">
            BACK TO TOP
          </span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Investments;
